/**
 * MCP tools for workflow proposals: list / get / diff / accept / reject /
 * revert. Mirrors `canary workflow proposals <verb>` with the same JSON
 * output shape so agents get identical responses whether they shell-out or
 * call MCP directly.
 *
 * @module
 */

import {
  diffSnapshots,
  fromApiSnapshot,
  type SnapshotDiff,
} from '@chatsdet/workflow-diff';
import { mcpApiRequest } from '../api-client';
import { toolJson } from '../tool-helpers';

// Types duplicated here to avoid the MCP server importing from the CLI's
// `workflow-proposals.ts` which would pull in TTY-detection + process.exit
// paths that don't belong in MCP land.
interface ProposalSummary {
  id: string;
  workflowId: string;
  title: string;
  description: string | null;
  source: string;
  status: string;
  createdByUserId: string | null;
  decidedByUserId: string | null;
  decidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface ProposalDetail extends ProposalSummary {
  snapshotJson: {
    nodes?: Array<{ id: string; nodeType: string; orderIndex?: number; configJson?: Record<string, unknown> }>;
    edges?: Array<{ id: string; sourceNodeId: string; targetNodeId: string; edgeType?: string | null }>;
  };
  sourceMetadata: Record<string, unknown> | null;
}

interface WorkflowDetail {
  id: string;
  nodes: Array<{ id: string; nodeType: string; orderIndex?: number; configJson?: Record<string, unknown> }>;
  edges: Array<{ id: string; sourceNodeId: string; targetNodeId: string; edgeType?: string | null }>;
}

export function getWorkflowProposalsToolDefinitions() {
  return [
    {
      name: 'canary_workflow_proposals_list',
      description:
        'List workflow proposals. By default returns pending proposals; pass status="all" for every status.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: { type: 'string', description: 'Workflow UUID' },
          status: {
            type: 'string',
            enum: ['pending', 'accepted', 'rejected', 'stale', 'all'],
            description: 'Filter by status (default: pending)',
          },
        },
        required: ['workflowId'],
      },
    },
    {
      name: 'canary_workflow_proposals_get',
      description: 'Fetch a proposal including its full snapshot JSON.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: { type: 'string' },
          proposalId: { type: 'string' },
        },
        required: ['workflowId', 'proposalId'],
      },
    },
    {
      name: 'canary_workflow_proposals_diff',
      description:
        'Compute a per-node diff between a proposal and the current workflow draft. Returns summary counts plus per-field changes for changed nodes.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: { type: 'string' },
          proposalId: { type: 'string' },
        },
        required: ['workflowId', 'proposalId'],
      },
    },
    {
      name: 'canary_workflow_proposals_accept',
      description:
        'Apply a proposal to the workflow draft. Omit acceptedNodeIds for atomic (all-or-nothing) accept; provide it to cherry-pick a subset.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: { type: 'string' },
          proposalId: { type: 'string' },
          acceptedNodeIds: {
            type: 'array',
            items: { type: 'string' },
            description:
              'Optional: node ids from the diff to include. Omit for atomic accept of every change.',
          },
        },
        required: ['workflowId', 'proposalId'],
      },
    },
    {
      name: 'canary_workflow_proposals_reject',
      description: 'Reject a pending proposal. Does not change the workflow draft.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: { type: 'string' },
          proposalId: { type: 'string' },
        },
        required: ['workflowId', 'proposalId'],
      },
    },
    {
      name: 'canary_workflow_proposals_revert',
      description:
        'Revert a previously-accepted proposal back to pending. Only the user who accepted it may revert.',
      inputSchema: {
        type: 'object',
        properties: {
          workflowId: { type: 'string' },
          proposalId: { type: 'string' },
        },
        required: ['workflowId', 'proposalId'],
      },
    },
  ];
}

export async function handleWorkflowProposalsTool(
  tool: string,
  args: Record<string, unknown>,
) {
  if (!tool.startsWith('canary_workflow_proposals_')) return null;

  try {
    switch (tool) {
      case 'canary_workflow_proposals_list':
        return await listProposals(args);
      case 'canary_workflow_proposals_get':
        return await getProposal(args);
      case 'canary_workflow_proposals_diff':
        return await diffProposal(args);
      case 'canary_workflow_proposals_accept':
        return await acceptProposal(args);
      case 'canary_workflow_proposals_reject':
        return await rejectProposal(args);
      case 'canary_workflow_proposals_revert':
        return await revertProposal(args);
      default:
        return null;
    }
  } catch (err) {
    return toolJson({
      ok: false,
      error: err instanceof Error ? err.message : String(err),
    });
  }
}

// ── Tool implementations ─────────────────────────────────────────────────

async function listProposals(args: Record<string, unknown>) {
  const workflowId = String(args.workflowId);
  const status = typeof args.status === 'string' ? args.status : 'pending';
  const query = status === 'all' ? '' : `?status=${encodeURIComponent(status)}`;
  const res = await mcpApiRequest<{
    ok: boolean;
    data?: ProposalSummary[];
    error?: string;
  }>('GET', `/workflows/${workflowId}/proposals${query}`);
  return toolJson({
    ok: res.ok,
    workflowId,
    count: res.data?.length ?? 0,
    proposals: res.data ?? [],
    error: res.error,
  });
}

async function getProposal(args: Record<string, unknown>) {
  const workflowId = String(args.workflowId);
  const proposalId = String(args.proposalId);
  const res = await mcpApiRequest<{
    ok: boolean;
    data?: ProposalDetail;
    error?: string;
  }>('GET', `/workflows/${workflowId}/proposals/${proposalId}`);
  return toolJson(res);
}

async function diffProposal(args: Record<string, unknown>) {
  const workflowId = String(args.workflowId);
  const proposalId = String(args.proposalId);
  const [propRes, wfRes] = await Promise.all([
    mcpApiRequest<{ ok: boolean; data?: ProposalDetail; error?: string }>(
      'GET',
      `/workflows/${workflowId}/proposals/${proposalId}`,
    ),
    mcpApiRequest<{ ok: boolean; workflow?: WorkflowDetail; error?: string }>(
      'GET',
      `/workflows/${workflowId}`,
    ),
  ]);
  if (!propRes.ok || !propRes.data) {
    return toolJson({ ok: false, error: propRes.error ?? 'proposal not found' });
  }
  if (!wfRes.workflow) {
    return toolJson({ ok: false, error: wfRes.error ?? 'workflow not found' });
  }
  const current = fromApiSnapshot({
    nodes: wfRes.workflow.nodes,
    edges: wfRes.workflow.edges,
  });
  const proposed = fromApiSnapshot(propRes.data.snapshotJson);
  const diff = diffSnapshots(current, proposed);
  return toolJson(buildDiffPayload(propRes.data, diff));
}

async function acceptProposal(args: Record<string, unknown>) {
  const workflowId = String(args.workflowId);
  const proposalId = String(args.proposalId);
  const acceptedNodeIds = Array.isArray(args.acceptedNodeIds)
    ? (args.acceptedNodeIds as string[]).filter((s) => typeof s === 'string')
    : null;
  const body = acceptedNodeIds
    ? { selection: { acceptedNodeIds } }
    : undefined;
  const res = await mcpApiRequest<{ ok: boolean; error?: string }>(
    'POST',
    `/workflows/${workflowId}/proposals/${proposalId}/accept`,
    body,
  );
  return toolJson({
    ...res,
    workflowId,
    proposalId,
    partial: acceptedNodeIds !== null,
  });
}

async function rejectProposal(args: Record<string, unknown>) {
  const workflowId = String(args.workflowId);
  const proposalId = String(args.proposalId);
  const res = await mcpApiRequest<{ ok: boolean; error?: string }>(
    'POST',
    `/workflows/${workflowId}/proposals/${proposalId}/reject`,
  );
  return toolJson({ ...res, workflowId, proposalId });
}

async function revertProposal(args: Record<string, unknown>) {
  const workflowId = String(args.workflowId);
  const proposalId = String(args.proposalId);
  const res = await mcpApiRequest<{ ok: boolean; error?: string }>(
    'POST',
    `/workflows/${workflowId}/proposals/${proposalId}/revert`,
  );
  return toolJson({ ...res, workflowId, proposalId });
}

// ── Diff payload shaper (shared shape with the CLI) ──────────────────────

function buildDiffPayload(proposal: ProposalDetail, diff: SnapshotDiff) {
  return {
    proposal: {
      id: proposal.id,
      title: proposal.title,
      source: proposal.source,
      status: proposal.status,
      stale: proposal.status === 'stale',
      createdAt: proposal.createdAt,
      decidedAt: proposal.decidedAt,
    },
    summary: {
      addedNodes: diff.addedNodes.length,
      removedNodes: diff.removedNodes.length,
      changedNodes: diff.changedNodes.length,
      addedEdges: diff.addedEdges.length,
      removedEdges: diff.removedEdges.length,
      touchedCount: diff.touchedCount,
      isEmpty: diff.isEmpty,
    },
    nodes: {
      added: diff.addedNodes.map((n) => ({
        id: n.id,
        nodeType: n.nodeType,
        configJson: n.configJson,
      })),
      removed: diff.removedNodes.map((n) => ({
        id: n.id,
        nodeType: n.nodeType,
        configJson: n.configJson,
      })),
      changed: diff.changedNodes.map((c) => ({
        id: c.id,
        nodeType: c.nodeType,
        typeChanged: c.typeChanged,
        beforeType: c.beforeType,
        afterType: c.afterType,
        fieldChanges: c.fieldChanges,
      })),
    },
    edges: {
      added: diff.addedEdges.map((e) => ({
        sourceNodeId: e.sourceNodeId,
        targetNodeId: e.targetNodeId,
        edgeType: e.edgeType ?? null,
      })),
      removed: diff.removedEdges.map((e) => ({
        sourceNodeId: e.sourceNodeId,
        targetNodeId: e.targetNodeId,
        edgeType: e.edgeType ?? null,
      })),
    },
  };
}
